import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { ArrowLeft } from "lucide-react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useManagerProfile } from "@/features/leagues/standings/hooks/useManagerProfile";
import { ManagerContributionsTable } from "@/features/leagues/managers/components/ManagerContributionsTable";
import { PageContainer } from "@/shared/layout/PageContainer";
import { PageHeader } from "@/shared/layout/PageHeader";
import { Skeleton } from "@/shared/ui/Skeleton";
import { useT } from "@/shared/i18n/LocaleContext";

export const Route = createFileRoute(
  "/_authenticated/leagues/$leagueId/managers/$memberId/contributions",
)({
  component: ManagerContributionsPage,
});

function ManagerContributionsPage() {
  const { leagueId, memberId } = Route.useParams();
  const t = useT();
  const profile = useManagerProfile(
    leagueId as Id<"leagues">,
    memberId as Id<"leagueMembers">,
  );
  const rows = useQuery(api.standings.listLeagueContributions, {
    leagueId: leagueId as Id<"leagues">,
    memberId: memberId as Id<"leagueMembers">,
    limit: 500,
    order: "asc",
  });

  if (profile === undefined || rows === undefined) {
    return (
      <PageContainer>
        <Skeleton className="mb-8 h-12 w-2/3" />
        <Skeleton className="h-96 w-full" />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Link
        to="/leagues/$leagueId/managers/$memberId"
        params={{ leagueId, memberId }}
        className="mb-4 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-muted no-underline transition-colors hover:text-ink"
      >
        <ArrowLeft size={14} />
        {t("league.profile.recent.backToManager")}
      </Link>

      <PageHeader
        eyebrow={profile.member.teamName}
        title={t("league.profile.recent.historyTitle")}
        size="default"
      />

      <ManagerContributionsTable rows={rows} leagueId={leagueId as Id<"leagues">} />
    </PageContainer>
  );
}
